import React, { useState, useEffect, useCallback } from 'react';

import { useSelector } from 'react-redux';

import { Button, Dropdown, Spinner, Alert, Row, Col, Table, Placeholder } from 'react-bootstrap';

import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaCheckCircle } from 'react-icons/fa';

import { confirmAlert } from "react-confirm-alert";

import styles from "./Cards.module.css";

import tableStyles from "../tickets/TicketsTable.module.css";

import plus from "../../../../css/img/plus.svg";

import axios from 'axios';

import { headers } from '../../../helpers/Requests';

import { baseURL } from '../../../helpers/baseURL';

import EditCard from './EditCard';
import { EMBED_FORMS_ACTIONS_MENU_POPPER } from '../embedForms/embedFormsActionsMenu';
import '../embedForms/embed-form-editor.css';



const resetActionsMenuReady = (dropdownToggleId) => {

	const el = document.getElementById(dropdownToggleId);

	if (!el) return;

	if (el.__embedMenuRevealRafId != null) {

		cancelAnimationFrame(el.__embedMenuRevealRafId);

		el.__embedMenuRevealRafId = null;

	}

	el.parentElement?.querySelector('.dropdown-menu')?.removeAttribute('data-embed-menu-ready');

};



const formatCardType = (type) => {
	if (!type || typeof type !== 'string') return '';
	return type.replace(/card$/i, '').trim();
};

const SkeletonBar = ({ xs = 6, size, className = '' }) => (
	<Placeholder
		xs={xs}
		size={size}
		className={`${styles.skeletonBar} ${className}`}
		style={{ display: 'block' }}
	/>
);



const Cards = ({ viewTitle, viewMode, onSelect, showCardModal, newCard, onLoadingChange }) => {



	const user = useSelector(state => state.userReducer?.user);

	const isOwner = Boolean(user?.isOwner);

	const isSelectMode = viewMode === 'select';



	const [paymentMethods, setPaymentMethods] = useState([]);

	const [isLoading, setIsLoading] = useState(false);

	const [rowLoading, setRowLoading] = useState([]);

	const [actionMessage, setActionMessage] = useState('');

	const [selectedCard, setSelectedCard] = useState('');

	const [editingCard, setEditingCard] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [openDropdownId, setOpenDropdownId] = useState(null);



	const changeLoadingStatus = (loadingStatus) => {

		setIsLoading(loadingStatus);

		if (typeof onLoadingChange === 'function') {

			onLoadingChange(loadingStatus);

		}

	};



	const loadPaymentMethods = useCallback(() => {

		changeLoadingStatus(true);

		axios.get(`${baseURL}payment-methods`, headers)

			.then(response => {

				setPaymentMethods(processCardsObject(response.data));

				setActionMessage('');

			})

			.catch(error => console.error(error))

			.finally(() => changeLoadingStatus(false));

	}, []);



	useEffect(() => {

		loadPaymentMethods();

	}, [loadPaymentMethods, newCard, refreshKey]);



	useEffect(() => {

		if (isSelectMode && paymentMethods.length === 1) {

			selectMethod(paymentMethods[0]);

		}

	}, [paymentMethods, isSelectMode]);



	const processCardsObject = (cards) => {

		return cards.map((card) => {

			const year = String(card.expiration_year || '');

			const month = String(card.expiration_month || '').padStart(2, '0');

			card.expiration_date = `${month}/${year.slice(-2)}`;

			if ('state' in card && typeof card.state === 'string') {

				const stateParts = card.state.split(', ');

				card.state = stateParts[0].trim();

				card.country = stateParts[1]?.trim() || card.country;

			}

			if (card.last_four) {

				card.last_four_numbers = String(card.last_four);

			} else if (card.card_number) {

				card.last_four_numbers = String(card.card_number).slice(-4);

			}

			card.is_primary = card.is_primary || card.is_default_payment_method;

			if (card.is_primary) {

				selectMethod(card);

			}

			return card;

		});

	};



	const selectMethod = (method) => {

		setSelectedCard(method);

		if (typeof onSelect === 'function') {

			onSelect(method);

		}

	};



	const showBlockedDeleteReason = (method) => {

		setActionMessage(method.delete_blocked_reason || 'This card cannot be deleted right now.');

	};



	const deletePaymentMethod = (method) => {

		if (method.can_delete === false) {

			showBlockedDeleteReason(method);

			return;

		}



		confirmAlert({

			title: 'Delete Payment Method',

			message: `···· ${method.last_four_numbers} will be deleted.`,

			buttons: [

				{

					label: 'Yes',

					onClick: () => {

						setRowLoading((prev) => [...prev, method.id]);

						axios.delete(`${baseURL}payment-methods/${method.id}`, headers)

							.then(() => {

								setPaymentMethods((prev) => prev.filter(m => m.id !== method.id));

								setActionMessage('');

							})

							.catch(error => {

								if (error.response?.data?.message) {

									setActionMessage(error.response.data.message);

								} else {

									console.error(error);

									setActionMessage('Something went wrong. Please try again later.');

								}

							})

							.finally(() => setRowLoading((prev) => prev.filter(id => id !== method.id)));

					}

				},

				{ label: 'No' }

			],

			overlayClassName: "confirm-overlay"

		});

	};



	const setDefaultPaymentMethod = (method) => {
		setRowLoading((prev) => [...prev, method.id]);
		axios.patch(`${baseURL}payment-methods/${method.id}`, {}, headers)
			.then(() => setRefreshKey((k) => k + 1))
			.catch(error => console.error(error))
			.finally(() => setRowLoading((prev) => prev.filter(id => id !== method.id)));
	};

	const openEditCard = (method) => {
		setEditingCard(method);
	};

	const handleRowClick = (method) => {
		if (isSelectMode) {
			selectMethod(method);
			return;
		}
		if (isOwner) {
			openEditCard(method);
		}
	};



	const columnCount = (isSelectMode ? 1 : 0) + 3 + (!isSelectMode && isOwner ? 1 : 0);

	const renderSkeletonRows = () => {
		const skeletonRowCount = 3;
		return Array.from({ length: skeletonRowCount }).map((_, idx) => (
			<tr key={`payment-method-skeleton-${idx}`} className={styles.skeletonRow} aria-hidden="true">
				{isSelectMode && (
					<td className={styles.selectCol}>
						<Placeholder as="div" animation="glow">
							<Placeholder className={styles.skeletonCircle} />
						</Placeholder>
					</td>
				)}
				<td className={styles.cardholderCell}>
					<Placeholder as="div" animation="glow">
						<SkeletonBar xs={7} />
					</Placeholder>
				</td>
				<td className={styles.cardNumberCell}>
					<Placeholder as="div" animation="glow">
						<SkeletonBar xs={8} />
					</Placeholder>
				</td>
				<td className={styles.expiresCell}>
					<Placeholder as="div" animation="glow">
						<SkeletonBar xs={5} />
					</Placeholder>
				</td>
				{!isSelectMode && isOwner && (
					<td className={tableStyles.stickyActions} style={{ textAlign: 'center' }}>
						<Placeholder as="div" animation="glow">
							<Placeholder.Button
								variant="secondary"
								className={styles.skeletonActionBtn}
								tabIndex={-1}
							/>
						</Placeholder>
					</td>
				)}
			</tr>
		));
	};

	const renderDefaultLabel = () => (
		<span
			className={styles.defaultStatus}
			title="Preferred card on file. Existing subscriptions may still bill another card until updated in iPaymer."
		>
			<FaCheckCircle className="text-success" aria-hidden="true" />
			Default
		</span>
	);

	const renderCardNumber = (method) => {
		const type = formatCardType(method.type);

		return (
			<span className={styles.cardNumberLine}>
				<span className={styles.cardNumber}>
					{type && <span className={styles.cardType}>{type}</span>}
					<span className={styles.cardDigits}>···· {method.last_four_numbers || '····'}</span>
				</span>
				{method.is_primary && renderDefaultLabel()}
			</span>
		);
	};



	return (

		<>

			<Row className='pt-4 pb-3'>

				<Col sm={9} className='text-align-left'>

					<h5>{viewTitle ? viewTitle : "Payment Methods"}</h5>
					{isOwner && (
						<p className={styles.helpText}>
							Default is your preferred card on file. Existing subscriptions may still bill a different card until that subscription is updated in iPaymer.
						</p>
					)}

				</Col>

				<Col sm={3}>

					{isOwner &&

						<Button variant='primary' className='float-end' onClick={() => showCardModal()}>

							<img src={plus} alt="" />

							<span className='ms-1'>Add New</span>

						</Button>

					}

				</Col>

			</Row>

			<Row>

				<Col className="overflow-x-auto">

					{actionMessage && (

						<Alert variant="danger" dismissible onClose={() => setActionMessage('')} className="mb-3">

							{actionMessage}

						</Alert>

					)}

					<div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap ${styles.tableWrap}`}>

						<Table className={`${tableStyles.table} ${styles.table}`} hover>

							<thead>

								<tr>

									{isSelectMode && <th className={styles.selectCol} aria-label="Select" />}

									<th className={styles.cardholderCell}>Cardholder</th>

									<th className={styles.cardNumberCell}>Card</th>

									<th className={styles.expiresCell}>Expires</th>

									{!isSelectMode && isOwner && (

										<th

											className={tableStyles.stickyActions}

											style={{ width: 110, textAlign: 'center' }}

										>

											Actions

										</th>

									)}

								</tr>

							</thead>

							<tbody>

								{isLoading ? (
									renderSkeletonRows()
								) : paymentMethods.length === 0 ? (

									<tr>

										<td colSpan={columnCount} className={styles.emptyCell}>

											{isSelectMode

												? 'No payment methods on file. Use Add New to add a card before confirming your plan change.'

												: 'No cards added yet.'}

										</td>

									</tr>

								) : (

									paymentMethods.map((method) => {

										const isSelected = method.id === selectedCard.id;

										const isRowLoading = rowLoading.indexOf(method.id) > -1;



										return (
											<tr
												key={method.id}
												className={
													isSelectMode
														? `${styles.selectRow} ${isSelected ? styles.selectRowSelected : ''}`
														: isOwner
															? styles.clickableRow
															: undefined
												}
												onClick={() => handleRowClick(method)}
											>

												{isSelectMode && (

													<td className={styles.selectCol} onClick={(e) => e.stopPropagation()}>

														<input

															readOnly

															type="radio"

															name="paymentMethod"

															value={method.id}

															checked={isSelected}

															onChange={() => selectMethod(method)}

															aria-label={`Select card ending in ${method.last_four_numbers}`}

														/>

													</td>

												)}

												<td className={styles.cardholderCell}>
													<span className={styles.cardholderName}>{method.name_on_card}</span>
												</td>

												<td className={styles.cardNumberCell}>

													{renderCardNumber(method)}

												</td>

												<td className={styles.expiresCell}>

													{method.expiration_month}/{method.expiration_year}

												</td>

												{!isSelectMode && isOwner && (

													<td
														className={`${tableStyles.stickyActions} ${openDropdownId === method.id ? styles.actionsCellOpen : ''}`}
														style={{ textAlign: 'center' }}
														onClick={(e) => e.stopPropagation()}
													>
														{isRowLoading ? (
															<Spinner animation="border" role="status" size="sm" />
														) : (
															<Dropdown
																align="end"
																show={openDropdownId === method.id}
																onToggle={(nextShow) => {
																	setOpenDropdownId(nextShow ? method.id : null);
																	if (!nextShow) {
																		resetActionsMenuReady(`payment-card-dd-${method.id}`);
																	}
																}}
															>

																<Dropdown.Toggle

																	as="button"

																	type="button"

																	className={tableStyles.actionBtn}

																	id={`payment-card-dd-${method.id}`}

																>

																	<BsThreeDotsVertical size={11} />

																</Dropdown.Toggle>

																<Dropdown.Menu

																	className="embed-ticket-forms-actions-menu"

																	popperConfig={EMBED_FORMS_ACTIONS_MENU_POPPER}

																>

																	<Dropdown.Item onClick={() => openEditCard(method)}>

																		Edit

																	</Dropdown.Item>

																	{!method.is_primary && (

																		<Dropdown.Item onClick={() => setDefaultPaymentMethod(method)}>

																			Set as default

																		</Dropdown.Item>

																	)}

																	<Dropdown.Item

																		className={method.can_delete === false ? `text-muted ${styles.actionDisabled}` : 'text-danger'}

																		onClick={() => deletePaymentMethod(method)}

																	>

																		Delete

																	</Dropdown.Item>

																</Dropdown.Menu>

															</Dropdown>

														)}

													</td>

												)}

											</tr>

										);

									})

								)}

							</tbody>

						</Table>

					</div>

				</Col>

			</Row>

			{!isSelectMode && (

				<EditCard

					card={editingCard}

					show={!!editingCard}

					onHide={() => setEditingCard(null)}

					onUpdated={() => setRefreshKey((k) => k + 1)}

				/>

			)}

		</>

	);

};



export default Cards;

